import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ApiError } from '../../api/client';
import { clientsApi, servicePlansApi, type ClientFormValues } from '../../api/endpoints';
import { useToast } from '../../app/ToastProvider';
import { todayInput } from '../../utils/format';
import type { ClientDetail } from '../../api/types';

interface ClientFormDialogProps {
  open: boolean;
  /** Omitted when creating a new client. */
  client?: ClientDetail;
  onClose: () => void;
}

interface FormState {
  customerType: 'RESIDENTIAL' | 'BUSINESS';
  firstName: string;
  middleName: string;
  lastName: string;
  businessName: string;
  contactPerson: string;
  mobileNumber: string;
  email: string;
  notes: string;
  isActive: boolean;
  addressLine1: string;
  addressLine2: string;
  barangay: string;
  municipality: string;
  province: string;
  postalCode: string;
  createSubscription: boolean;
  servicePlanId: string;
  subscriptionStatus: string;
  billingDay: string;
  installationDate: string;
  activationDate: string;
  pppoeUsername: string;
  pppoePassword: string;
  installationFee: string;
  referralFee: string;
  installerCommission: string;
  agentReferral: string;
  installerNames: string;
  smt: string;
  lcp: string;
  nap: string;
  portNumber: string;
  autoSuspendEnabled: boolean;
  gracePeriodDays: string;
  routerSerialNumber: string;
  onuSerialNumber: string;
}

const EMPTY_FORM: FormState = {
  customerType: 'RESIDENTIAL',
  firstName: '',
  middleName: '',
  lastName: '',
  businessName: '',
  contactPerson: '',
  mobileNumber: '',
  email: '',
  notes: '',
  isActive: true,
  addressLine1: '',
  addressLine2: '',
  barangay: '',
  municipality: '',
  province: 'Bulacan',
  postalCode: '',
  createSubscription: true,
  servicePlanId: '',
  subscriptionStatus: 'PENDING',
  billingDay: '10',
  installationDate: '',
  activationDate: '',
  pppoeUsername: '',
  pppoePassword: '',
  installationFee: '0',
  referralFee: '0',
  installerCommission: '0',
  agentReferral: '',
  installerNames: '',
  smt: '',
  lcp: '',
  nap: '',
  portNumber: '',
  autoSuspendEnabled: true,
  gracePeriodDays: '6',
  routerSerialNumber: '',
  onuSerialNumber: '',
};

function toFormState(client: ClientDetail): FormState {
  const address = client.addresses.find((item) => item.isPrimary) ?? client.addresses[0];
  return {
    ...EMPTY_FORM,
    customerType: client.customerType,
    firstName: client.firstName ?? '',
    middleName: client.middleName ?? '',
    lastName: client.lastName ?? '',
    businessName: client.businessName ?? '',
    contactPerson: client.contactPerson ?? '',
    mobileNumber: client.mobileNumber,
    email: client.email ?? '',
    notes: client.notes ?? '',
    isActive: client.isActive,
    addressLine1: address?.addressLine1 ?? '',
    addressLine2: address?.addressLine2 ?? '',
    barangay: address?.barangay ?? '',
    municipality: address?.municipality ?? '',
    province: address?.province ?? 'Bulacan',
    postalCode: address?.postalCode ?? '',
    createSubscription: false,
  };
}

export function ClientFormDialog({ open, client, onClose }: ClientFormDialogProps) {
  const isEdit = Boolean(client);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: plans = [] } = useQuery({
    queryKey: ['service-plans', 'active'],
    queryFn: () => servicePlansApi.list(false),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setForm(client ? toFormState(client) : EMPTY_FORM);
    setFieldErrors({});
    setError(null);
  }, [open, client]);

  const isBusiness = form.customerType === 'BUSINESS';

  const payload = useMemo<ClientFormValues>(() => {
    const base: ClientFormValues = {
      customerType: form.customerType,
      firstName: isBusiness ? undefined : form.firstName.trim() || undefined,
      middleName: isBusiness ? undefined : form.middleName.trim() || undefined,
      lastName: isBusiness ? undefined : form.lastName.trim() || undefined,
      businessName: isBusiness ? form.businessName.trim() || undefined : undefined,
      contactPerson: isBusiness ? form.contactPerson.trim() || undefined : undefined,
      mobileNumber: form.mobileNumber.trim(),
      email: form.email.trim() || undefined,
      notes: form.notes.trim() || undefined,
      address: {
        addressLine1: form.addressLine1.trim(),
        addressLine2: form.addressLine2.trim() || undefined,
        barangay: form.barangay.trim() || undefined,
        municipality: form.municipality.trim() || undefined,
        province: form.province.trim() || undefined,
        postalCode: form.postalCode.trim() || undefined,
      },
    };

    if (isEdit) return { ...base, isActive: form.isActive };

    if (form.createSubscription && form.servicePlanId) {
      base.subscription = {
        servicePlanId: Number(form.servicePlanId),
        status: form.subscriptionStatus,
        billingDay: Number(form.billingDay),
        installationDate: form.installationDate || undefined,
        activationDate: form.activationDate || undefined,
        pppoeUsername: form.pppoeUsername.trim() || undefined,
        installationFee: Number(form.installationFee || 0),
        referralFee: Number(form.referralFee || 0),
        installerCommission: Number(form.installerCommission || 0),
        agentReferral: form.agentReferral.trim() || undefined,
        installerNames: form.installerNames.trim() || undefined,
        smt: form.smt.trim() || undefined,
        lcp: form.lcp.trim() || undefined,
        nap: form.nap.trim() || undefined,
        portNumber: form.portNumber.trim() || undefined,
        autoSuspendEnabled: form.autoSuspendEnabled,
        gracePeriodDays: Number(form.gracePeriodDays || 0),
        pppoePassword: form.pppoePassword.trim() || undefined,
        routerSerialNumber: form.routerSerialNumber.trim() || undefined,
        onuSerialNumber: form.onuSerialNumber.trim() || undefined,
      };
    }

    return base;
  }, [form, isBusiness, isEdit]);

  const mutation = useMutation({
    mutationFn: () =>
      client ? clientsApi.update(client.id, payload) : clientsApi.create(payload),
    onSuccess: async ({ message }) => {
      toast.success(message);
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      await queryClient.invalidateQueries({ queryKey: ['client', client?.id] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.byField());
        setError(caught.message);
      } else {
        setError('The client could not be saved.');
      }
    },
  });

  const set =
    (key: keyof FormState) =>
    (event: { target: { value: string } }): void => {
      setForm((current) => ({ ...current, [key]: event.target.value }));
    };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>{isEdit ? `Edit ${client?.clientNumber}` : 'Add Client'}</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Customer Information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label="Customer Type"
                value={form.customerType}
                onChange={set('customerType')}
                required
              >
                <MenuItem value="RESIDENTIAL">Residential</MenuItem>
                <MenuItem value="BUSINESS">Business</MenuItem>
              </TextField>
            </Grid>

            {isBusiness ? (
              <>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField
                    label="Business Name"
                    value={form.businessName}
                    onChange={set('businessName')}
                    error={Boolean(fieldErrors.businessName)}
                    helperText={fieldErrors.businessName}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Contact Person"
                    value={form.contactPerson}
                    onChange={set('contactPerson')}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="First Name"
                    value={form.firstName}
                    onChange={set('firstName')}
                    error={Boolean(fieldErrors.firstName)}
                    helperText={fieldErrors.firstName}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField label="Middle Name" value={form.middleName} onChange={set('middleName')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Last Name"
                    value={form.lastName}
                    onChange={set('lastName')}
                    error={Boolean(fieldErrors.lastName)}
                    helperText={fieldErrors.lastName}
                    required
                  />
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Mobile Number"
                value={form.mobileNumber}
                onChange={set('mobileNumber')}
                error={Boolean(fieldErrors.mobileNumber)}
                helperText={fieldErrors.mobileNumber ?? 'Example: 09171234567'}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email Address"
                type="email"
                value={form.email}
                onChange={set('email')}
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Service Address
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label="Address Line 1"
                value={form.addressLine1}
                onChange={set('addressLine1')}
                error={Boolean(fieldErrors['address.addressLine1'])}
                helperText={fieldErrors['address.addressLine1']}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Address Line 2" value={form.addressLine2} onChange={set('addressLine2')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Barangay" value={form.barangay} onChange={set('barangay')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Municipality" value={form.municipality} onChange={set('municipality')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Province" value={form.province} onChange={set('province')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Postal Code" value={form.postalCode} onChange={set('postalCode')} />
            </Grid>
          </Grid>

          {isEdit ? null : (
            <>
              <Divider sx={{ my: 3 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.createSubscription}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        createSubscription: event.target.checked,
                      }))
                    }
                  />
                }
                label="Create an internet subscription for this client"
              />

              {form.createSubscription ? (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <TextField
                      select
                      label="Service Plan"
                      value={form.servicePlanId}
                      onChange={set('servicePlanId')}
                      error={Boolean(fieldErrors['subscription.servicePlanId'])}
                      helperText={fieldErrors['subscription.servicePlanId']}
                      required
                    >
                      {plans.map((plan) => (
                        <MenuItem key={plan.id} value={String(plan.id)}>
                          {plan.planName} — PHP {plan.monthlyFee.toFixed(2)}/month
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      select
                      label="Subscription Status"
                      value={form.subscriptionStatus}
                      onChange={set('subscriptionStatus')}
                    >
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="ACTIVE">Active</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      label="Billing Day"
                      type="number"
                      value={form.billingDay}
                      onChange={set('billingDay')}
                      error={Boolean(fieldErrors['subscription.billingDay'])}
                      helperText={fieldErrors['subscription.billingDay'] ?? 'Day of month, 1–31'}
                      slotProps={{ htmlInput: { min: 1, max: 31 } }}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Installation Date"
                      type="date"
                      value={form.installationDate}
                      onChange={set('installationDate')}
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: todayInput() } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Activation Date"
                      type="date"
                      value={form.activationDate}
                      onChange={set('activationDate')}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="PPPoE Username"
                      value={form.pppoeUsername}
                      onChange={set('pppoeUsername')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="PPPoE Password" value={form.pppoePassword} onChange={set('pppoePassword')} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="Installation Fee" type="number" value={form.installationFee} onChange={set('installationFee')} slotProps={{ htmlInput: { min: 0, step: '0.01' } }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="Agent / Referral" value={form.agentReferral} onChange={set('agentReferral')} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="Referral Fee" type="number" value={form.referralFee} onChange={set('referralFee')} slotProps={{ htmlInput: { min: 0, step: '0.01' } }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="Installer(s) Name" value={form.installerNames} onChange={set('installerNames')} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="Installer Commission" type="number" value={form.installerCommission} onChange={set('installerCommission')} slotProps={{ htmlInput: { min: 0, step: '0.01' } }} />
                  </Grid>
                  {(['smt', 'lcp', 'nap', 'portNumber'] as const).map((key) => (
                    <Grid key={key} size={{ xs: 12, sm: 3 }}>
                      <TextField label={key === 'portNumber' ? 'Port #' : key.toUpperCase()} value={form[key]} onChange={set(key)} />
                    </Grid>
                  ))}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel control={<Switch checked={form.autoSuspendEnabled} onChange={(event) => setForm((current) => ({ ...current, autoSuspendEnabled: event.target.checked }))} />} label="Automatically suspend after grace period" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label="Grace Period (days)" type="number" value={form.gracePeriodDays} onChange={set('gracePeriodDays')} disabled={!form.autoSuspendEnabled} slotProps={{ htmlInput: { min: 0, max: 365 } }} />
                  </Grid>                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Router Serial Number"
                      value={form.routerSerialNumber}
                      onChange={set('routerSerialNumber')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="ONU Serial Number"
                      value={form.onuSerialNumber}
                      onChange={set('onuSerialNumber')}
                    />
                  </Grid>
                </Grid>
              ) : null}
            </>
          )}

          <Divider sx={{ my: 3 }} />
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="Notes"
                value={form.notes}
                onChange={set('notes')}
                multiline
                minRows={2}
              />
            </Grid>
            {isEdit ? (
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, isActive: event.target.checked }))
                      }
                    />
                  }
                  label="Client account is active"
                />
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Client'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
